<template>
  <GoDialog class="LocaleSelectModal" backgroundColor="dark-blue" ref="dialog" @closed="localeSelectClosed">
    <div class="layout" v-if="preferredLocale">
      <div class="localeMessage">
        <h2>{{ content.greeting }}</h2>
        <h2>{{ content.message }}</h2>
        <div class="country">
          <FlagIcon class="flagIcon" :locale="preferredLocale" />
          <span class="countryName">{{ countryName }}</span>
        </div>
        <div class="buttons">
          <LargeCtaButton class="continueButton short --bright-blue" icon="arrow" :text="content.continue" :action="rootUrl" />
          <LargeCtaButton class="continueButton long --bright-blue" icon="arrow" :text="content.continueLong" :action="rootUrl" />
          <button @click="$refs.dialog.close()" class="stayButton">{{ stayButtonText }}</button>
        </div>
      </div>
      <div class="localeMap">
        <LocaleMap :locale="preferredLocale" />
      </div>
    </div>
  </GoDialog>
</template>

<script>
import getCookie from '~/lib/getCookie.js'

import localeInfo from '~/data/localeInfo.json'
import contentAllLocales from './content.json'

import GoDialog from '~/vue-components/shared/GoDialog.vue'
import FlagIcon from '../FlagIcon.vue'
import LocaleMap from './LocaleMap.vue'

const disabledPathPrefixes = [
  '/resources/product-documentation/',  // The product doc. is for both US and non-US customers
]

function getPreferredLocale() {
  const geoCookie = getCookie('inseegoGeo')
  switch (geoCookie) {
    case 'AU': return 'en-AU'
    case 'BE': return 'nl-BE'
    case 'DE': return 'de-DE'
    case 'GB': return 'en-GB'
    case 'IE': return 'en-IE'
    case 'NZ': return 'en-NZ'
    case 'NL': return 'nl-NL'
    case 'UM':
    case 'US': return 'en-US'
  }
}

function shouldShowLocaleSelect(preferredLocale) {

  // This allows us to force the modal to be shown or not shown.
  // Mostly so we can disable it locally while working on the site.
  switch (localStorage['developerLocaleSelectOverride']) {
  case 'enabled':
    return true
  case 'disabled':
    return false
  }

  // Don't show the modal on paths that start with any of these prefixes
  if (disabledPathPrefixes.some((pathPrefix) => document.location.pathname.startsWith(pathPrefix))) {
    return false
  }

  // The visitor has already seen and dismissed the modal on this locale
  if (sessionStorage['localeSelectDismissed'] === document.documentElement.lang) {
    return false
  }

  return document.documentElement.lang !== preferredLocale
}

export default {
  components: {
    GoDialog,
    FlagIcon,
    LocaleMap,
  },
  inject: ['locale'],
  data() {
    return {
      preferredLocale: null,
    }
  },
  mounted() {
    const preferredLocale = getPreferredLocale()
    if (preferredLocale) {
      this.preferredLocale = preferredLocale
      if (shouldShowLocaleSelect(this.preferredLocale)) {
        this.$refs.dialog.showModal()
      }
    }
  },
  computed: {
    content() {
      return contentAllLocales[this.preferredLocale] || {}
    },
    countryName() {
      return localeInfo[this.preferredLocale].countryName
    },
    rootUrl() {
      return localeInfo[this.preferredLocale].rootUrl
    },
    stayButtonText() {
      const { countryName } = localeInfo[this.locale]
      return this.content.stayOnCurrentSite?.replace('{countryName}', countryName)
    },
  },
  methods: {
    localeSelectClosed() {
      // Store the current locale being dismissed
      // Only allow one locale to be dismissed at a time
      sessionStorage['localeSelectDismissed'] = document.documentElement.lang
    },
  },
}
</script>

<style scoped>

.layout {
  display: grid;
  align-items: center;
  margin: var(--spacing-13) var(--spacing-9);
}

.localeMessage {
  grid-row: 2;
  padding-top: var(--spacing-8);
  color: var(--text-color-white);
}

.localeMessage h2 {
  font-size: var(--font-size-8);
  font-weight: 600;
  line-height: 1.45;
}

.country {
  display: flex;
  align-items: center;
  padding-top: var(--spacing-12);
  padding-bottom: var(--spacing-16);
}

.flagIcon {
  width: 38px;
}

.countryName {
  margin-left: 1em;
  font-size: var(--font-size-3);
}

.buttons {
  display: flex;
  flex-direction: column;
  align-items: start;
}

.continueButton.long {
  display: none;
}

.stayButton {
  margin-top: var(--spacing-10);
  font-weight: 400;
  font-size: var(--font-size-3);
  color: var(--text-color-white);
}

.localeMap {
  grid-row: 1;
}

@media (--screen-sm) {

  .layout {
    grid-template-columns: repeat(12, 1fr);
    margin-right: var(--spacing-12);
    margin-left: var(--spacing-16);
  }

  .localeMessage {
    grid-row: 1;
    grid-column: 1 / span 5;
    padding-top: 0;
  }

  .country {
    padding-top: var(--spacing-9);
    padding-bottom: var(--spacing-11);
  }

  .continueButton.short {
    display: none;
  }

  .continueButton.long {
    display: inline-block;
  }

  .localeMap {
    grid-row: 1;
    grid-column: 6 / span 7;
  padding-left: var(--spacing-9);
  }
}
</style>
